import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DirectionProvider } from '@radix-ui/react-direction';

const DIRECTIONALITIES = ['ltr', 'rtl'] as const;

type TextDirectionality = (typeof DIRECTIONALITIES)[number];

const isTextDirectionality = (value: string): value is TextDirectionality =>
  DIRECTIONALITIES.includes(value as TextDirectionality);

const DEFAULT_TEXT_DIRECTIONALITY: TextDirectionality = 'ltr';

const TextDirectionalityContext = createContext<TextDirectionality>(
  DEFAULT_TEXT_DIRECTIONALITY,
);

const getElementTextDirectionality = (
  element?: HTMLElement,
): TextDirectionality => {
  const ourElement =
    element || (typeof window !== 'undefined' && window.document.body);

  if (!ourElement) {
    return DEFAULT_TEXT_DIRECTIONALITY;
  }

  let currentElement: HTMLElement | null = ourElement;

  while (currentElement) {
    if (isTextDirectionality(currentElement.dir)) {
      return currentElement.dir;
    }

    currentElement = currentElement.parentElement;
  }

  return DEFAULT_TEXT_DIRECTIONALITY;
};

export const TextDirectionalityProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [textDirectionality, setTextDirectionality] = useState(
    getElementTextDirectionality,
  );

  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (ref.current) {
      setTextDirectionality(getElementTextDirectionality(ref.current));
    }
  }, []);

  return (
    <TextDirectionalityContext.Provider value={textDirectionality}>
      <DirectionProvider dir={textDirectionality}>{children}</DirectionProvider>
      <span aria-hidden="true" id="text-directionality-detector" ref={ref} />
    </TextDirectionalityContext.Provider>
  );
};

export const useTextDirectionality = () =>
  useContext(TextDirectionalityContext);
